import React from 'react';
import { cn } from '../../utils/cn';

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

const sideClasses: Record<NonNullable<TooltipProps['side']>, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const Tooltip: React.FC<TooltipProps> = ({ content, children, side = 'top', className }) => {
  const [open, setOpen] = React.useState(false);
  const tooltipId = React.useId();

  const hide = React.useCallback(() => setOpen(false), []);
  const show = React.useCallback(() => setOpen(true), []);

  const currentDescribedBy = children.props['aria-describedby'] as string | undefined;
  const describedBy = open
    ? [currentDescribedBy, tooltipId].filter(Boolean).join(' ')
    : currentDescribedBy;

  const clonedChild = React.cloneElement(children, {
    'aria-describedby': describedBy || undefined,
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      show();
      children.props.onFocus?.(event);
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      hide();
      children.props.onBlur?.(event);
    },
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      show();
      children.props.onMouseEnter?.(event);
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      hide();
      children.props.onMouseLeave?.(event);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        hide();
      }
      children.props.onKeyDown?.(event);
    },
  });

  return (
    <span className="relative inline-flex">
      {clonedChild}
      <span
        role="tooltip"
        id={tooltipId}
        className={cn(
          'pointer-events-none absolute z-50 max-w-xs rounded-lg bg-neutral-900 px-3 py-2 text-xs font-medium text-white shadow-lg transition-opacity duration-150',
          open ? 'opacity-100' : 'opacity-0',
          sideClasses[side],
          className,
        )}
        aria-hidden={open ? 'false' : 'true'}
      >
        {content}
      </span>
    </span>
  );
};

export { Tooltip };
